import { useForm } from "react-hook-form";
import { RegisterSchema } from "../../schemas/interview-form.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form/form";
import { Input } from "../ui/input/input";
import { Checkbox } from "../ui/input/Checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/input/select";
import { StateList } from "../../utils/StatesList";
import { toast } from "sonner";
import { interviewRegister } from "../../api/interview";
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import SubmissionSuccess from "./SubmissionSuccess";

const InterviewForm = () => {
  const navigate = useNavigate();

  const [view, setView] = useState("form");
  const [checked, setChecked] = useState(false);

  const form = useForm({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      name: "",
      phone_no: "",
      whatsapp_number: "",
      email: "",
      education_qualification: "",
      other_qualification: "",
      state: "",
      district: "",
      city: "",
      pin: "",
    },
  });

  const renderView = () => {
    switch (view) {
      case "form":
        return (
          <div
            className="bg-white/70 backdrop-blur-xl shadow-xl rounded-2xl 
        p-4 sm:p-8 md:p-10 border border-neutral-200"
          >
            <div className="mb-3 text-center">
              <Link
                to="/career"
                className="text-red-600 hover:underline font-semibold text-md animate-pulse"
              >
                Click to Review Job Details Before Submission
              </Link>
            </div>
            <p className="text-lg font-semibold text-secondary text-center mb-0">
              District Wellness Officer (DWO)
            </p>
            <h2 className="text-xl lg:text-2xl font-semibold text-secondary mb-6 text-center">
              Register Yourself for{" "}
              <span className="text-[#307bc4]">Online Interview</span>
            </h2>

            <Form {...form}>
              <form
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                {/* -------- Full Name -------- */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Full Name"
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- Email -------- */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="example@email.com"
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Phone Number */}
                <FormField
                  control={form.control}
                  name="phone_no"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="9876543210"
                          type="tel"
                          maxLength={10}
                          className="cs_form_field h-11"
                          {...field}
                          onInput={(e) => {
                            e.currentTarget.value = e.currentTarget.value
                              .replace(/\D/g, "")
                              .slice(0, 10);

                            field.onChange(e.currentTarget.value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* WhatsApp Number */}
                <FormField
                  control={form.control}
                  name="whatsapp_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex gap-4 items-center mb-4">
                        <span>WhatsApp Number</span>

                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => setChecked(!checked)}
                            className="rounded-xl size-4"
                          />
                          <span className="text-xs">Same as Contact</span>
                        </label>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="9876543210"
                          type="tel"
                          maxLength={10}
                          className="cs_form_field h-11"
                          {...field}
                          onInput={(e) => {
                            e.currentTarget.value = e.currentTarget.value
                              .replace(/\D/g, "")
                              .slice(0, 10);

                            if (!checked) {
                              field.onChange(e.currentTarget.value);
                            }
                          }}
                          disabled={checked}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* ------Education Info------- */}
                <FormField
                  control={form.control}
                  name="education_qualification"
                  render={({ field }) => (
                    <FormItem className="grow">
                      <FormLabel>Education Qualification</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select Qualification" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[
                            "HS (Science)",
                            "Graduation (Science)",
                            "Graduation (Arts)",
                            "Graduation (Commerce)",
                            "Other",
                          ].map((q) => {
                            return (
                              <SelectItem key={q} value={q} className="w-full">
                                {q}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {education === "Other" && (
                  <FormField
                    control={form.control}
                    name="other_qualification"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Other Qualification</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter Other Qualification"
                            className="cs_form_field h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* -------- State -------- */}
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("district", ""); // reset district
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select State" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {StateList.map((state) => {
                            return (
                              <SelectItem key={state.state} value={state.state}>
                                {state.state}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- District -------- */}
                <FormField
                  control={form.control}
                  name="district"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>District</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!selectedState}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue
                              placeholder={
                                form.getValues("state")
                                  ? "Select District"
                                  : "First select a State"
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {form.getValues("state") &&
                            StateList.find(
                              (item) => item.state === selectedState,
                            )?.districts.map((district, index) => (
                              <SelectItem key={index} value={district}>
                                {district}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- City -------- */}
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City / Town</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter City"
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- PIN -------- */}
                <FormField
                  control={form.control}
                  name="pin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PIN Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="6-digit PIN"
                          maxLength={6}
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- Submit Button -------- */}
                <div className="col-span-1 md:col-span-2 flex justify-center mt-4">
                  <button
                    className="shadow-lg border px-3 w-60 py-2 bg-[#274760] rounded-xl"
                    disabled={form.formState.isSubmitting}
                  >
                    <span className="text-center text-sm lg:text-lg text-white font-medium tracking-tight">
                      {form.formState.isSubmitting ? (
                        <span className="flex items-center gap-2 justify-center">
                          Registering...
                        </span>
                      ) : (
                        "Register"
                      )}
                    </span>
                  </button>
                </div>
              </form>
            </Form>
          </div>
        );
      case "success":
        return (
          <SubmissionSuccess />
        );
      default:
        return (
          <div
            className="bg-white/70 backdrop-blur-xl shadow-xl rounded-2xl 
        p-4 sm:p-8 md:p-10 border border-neutral-200"
          >
            <div className="mb-3 text-center">
              <Link
                to="/career"
                className="text-red-600 hover:underline font-semibold text-md animate-pulse"
              >
                Click to Review Job Details Before Submission
              </Link>
            </div>
            <p className="text-lg font-semibold text-secondary text-center mb-0">
              District Wellness Officer (DWO)
            </p>
            <h2 className="text-xl lg:text-2xl font-semibold text-secondary mb-6 text-center">
              Register Yourself for{" "}
              <span className="text-[#307bc4]">Online Interview</span>
            </h2>

            <Form {...form}>
              <form
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                {/* -------- Full Name -------- */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Full Name"
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- Email -------- */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="example@email.com"
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Phone Number */}
                <FormField
                  control={form.control}
                  name="phone_no"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="9876543210"
                          type="tel"
                          maxLength={10}
                          className="cs_form_field h-11"
                          {...field}
                          onInput={(e) => {
                            e.currentTarget.value = e.currentTarget.value
                              .replace(/\D/g, "")
                              .slice(0, 10);

                            field.onChange(e.currentTarget.value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* WhatsApp Number */}
                <FormField
                  control={form.control}
                  name="whatsapp_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex gap-4 items-center mb-4">
                        <span>WhatsApp Number</span>

                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => setChecked(!checked)}
                            className="rounded-xl size-4"
                          />
                          <span className="text-xs">Same as Contact</span>
                        </label>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="9876543210"
                          type="tel"
                          maxLength={10}
                          className="cs_form_field h-11"
                          {...field}
                          onInput={(e) => {
                            e.currentTarget.value = e.currentTarget.value
                              .replace(/\D/g, "")
                              .slice(0, 10);

                            if (!checked) {
                              field.onChange(e.currentTarget.value);
                            }
                          }}
                          disabled={checked}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* ------Education Info------- */}
                <FormField
                  control={form.control}
                  name="education_qualification"
                  render={({ field }) => (
                    <FormItem className="grow">
                      <FormLabel>Education Qualification</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select Qualification" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[
                            "HS (Science)",
                            "Graduation (Science)",
                            "Graduation (Arts)",
                            "Graduation (Commerce)",
                            "Other",
                          ].map((q) => {
                            return (
                              <SelectItem key={q} value={q} className="w-full">
                                {q}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {education === "Other" && (
                  <FormField
                    control={form.control}
                    name="other_qualification"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Other Qualification</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter Other Qualification"
                            className="cs_form_field h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* -------- State -------- */}
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("district", ""); // reset district
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select State" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {StateList.map((state) => {
                            return (
                              <SelectItem key={state.state} value={state.state}>
                                {state.state}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- District -------- */}
                <FormField
                  control={form.control}
                  name="district"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>District</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!selectedState}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue
                              placeholder={
                                form.getValues("state")
                                  ? "Select District"
                                  : "First select a State"
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {form.getValues("state") &&
                            StateList.find(
                              (item) => item.state === selectedState,
                            )?.districts.map((district, index) => (
                              <SelectItem key={index} value={district}>
                                {district}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- City -------- */}
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City / Town</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter City"
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- PIN -------- */}
                <FormField
                  control={form.control}
                  name="pin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PIN Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="6-digit PIN"
                          maxLength={6}
                          className="cs_form_field h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* -------- Submit Button -------- */}
                <div className="col-span-1 md:col-span-2 flex justify-center mt-4">
                  <button
                    className="shadow-lg border px-3 w-60 py-2 bg-[#274760] rounded-xl"
                    disabled={form.formState.isSubmitting}
                  >
                    <span className="text-center text-sm lg:text-lg text-white font-medium tracking-tight">
                      {form.formState.isSubmitting ? (
                        <span className="flex items-center gap-2 justify-center">
                          Registering...
                        </span>
                      ) : (
                        "Register"
                      )}
                    </span>
                  </button>
                </div>
              </form>
            </Form>
          </div>
        );
    }
  };

  const selectedState = form.watch("state");
  const phoneNo = form.watch("phone_no");
  const education = form.watch("education_qualification");

  useEffect(() => {
    if (checked) {
      form.setValue("whatsapp_number", phoneNo);
    }
  }, [form, phoneNo, checked]);

  const navigateTimer = useRef();

  useEffect(() => {
    if (view === "success") {
      navigateTimer.current = setTimeout(() => {
        navigate("/career");
      }, 3000);
    }
    return () => clearTimeout(navigateTimer.current);
  }, [view, navigate]);

  const onSubmit = async (values) => {
    try {
      const res = await interviewRegister(values);

      if (!res) {
        toast.error("Registration failed! Please try again.");
        throw new Error("Registration failed");
      }

      if (res?.status === 204 || res?.status === 200) {
        toast.success("Successfully Registered for Interview");
        form.reset();
        setView("success");
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 400) {
        toast.error("You have already registered for the Online Interview!");
      }
    }
  };

  return <div className="max-w-5xl mx-auto pt-32 pb-16 px-2">{renderView()}</div>;
};

export default InterviewForm;
